import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Building2, MapPin, Globe, Users, FileText, ChevronLeft, Loader2,
  Briefcase, Calendar, Star, CheckCircle, Mail, Phone
} from 'lucide-react';

const CompanyProfile = () => {
  const { employerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    fetchCompanyProfile();
  }, [employerId]);

  const fetchCompanyProfile = async () => {
    try {
      const res = await employeeAPI.getCompanyProfile(employerId);
      setCompany(res.data.company || null);
      setRecords(res.data.records || []);
    } catch (error) {
      console.error('Error fetching company profile:', error);
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!company) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-[#5B6472] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0A0F1E] mb-2">Company not found</h2>
          <Button onClick={() => navigate('/employee/connected')} variant="outline" className="border-[#E8EDF5] text-[#0A0F1E]">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Connected
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="company-profile-page">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/employee/connected')}
          className="text-[#5B6472] hover:text-[#0A0F1E] hover:bg-transparent -ml-2"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Connected
        </Button>

        {/* Company Header */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {company.logo_url ? (
              <img 
                src={company.logo_url} 
                alt={company.company_name}
                className="w-24 h-24 rounded-xl object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                <Building2 className="w-12 h-12 text-[#5B6472]" />
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-[#0A0F1E]">{company.company_name}</h1>
              {company.industry && (
                <p className="text-[#1B5AFF] font-medium mt-1">{company.industry}</p>
              )}
              
              <div className="flex flex-wrap items-center gap-4 mt-3 text-[#5B6472] text-sm">
                {company.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {company.location}
                  </span>
                )}
                {company.company_size && (
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {company.company_size} employees
                  </span>
                )}
                {company.website && (
                  <a 
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:text-[#1B5AFF] transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                  </a>
                )}
              </div>
              
              {company.bio && (
                <p className="text-[#5B6472] mt-4 leading-relaxed">{company.bio}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Your Records with this Company */}
        <div>
          <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1B5AFF]" />
            Your VETT Records with {company.company_name}
          </h2>
          
          {records.length === 0 ? (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 text-center">
              <FileText className="w-10 h-10 text-[#5B6472] mx-auto mb-3" />
              <p className="text-[#5B6472]">No verified records yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {records.map((record) => (
                <Card 
                  key={record.id}
                  className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-semibold text-lg">{record.job_title}</h3>
                      {record.department && (
                        <p className="text-[#5B6472] text-sm">{record.department}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[#5B6472] text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(record.start_date)} - {record.end_date ? formatDate(record.end_date) : 'Present'}
                        </span>
                        {record.employment_type && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {record.employment_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {record.rating && (
                        <div className="flex items-center gap-1 text-amber-400">
                          <Star className="w-4 h-4 fill-amber-400" />
                          <span className="font-medium">{record.rating}/5</span>
                        </div>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        record.status === 'acknowledged' 
                          ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                          : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      }`}>
                        {record.status === 'acknowledged' ? 'Verified' : 'Pending'}
                      </span>
                    </div>
                  </div>
                  
                  {record.strengths?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#E8EDF5]">
                      <p className="text-[#5B6472] text-xs mb-2">Strengths:</p>
                      <div className="flex flex-wrap gap-2">
                        {record.strengths.map((strength, i) => (
                          <span key={i} className="px-3 py-1 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-xs border border-[#E8EDF5]">
                            {strength}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyProfile;
